import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useProperties, PROPERTIES_PAGE_SIZE as PAGE_SIZE } from "@/hooks/use-properties";
import { PropertyDrawer } from "./PropertyDrawer";
import { PropertyCreateDialog } from "./PropertyCreateDialog";
import { PropertyEditDialog } from "./PropertyEditDialog";
import { apiClient } from "@/lib/api-client";
import { MapPin, Plus, Search, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import type { Property } from "@/api/generated/client/models";

function formatAddress(p: Property): string {
    const parts = [p.streetAddress];
    if (p.unit) parts[0] += ` ${p.unit}`;
    const cityState = [p.city, p.state].filter(Boolean).join(", ");
    if (cityState) parts.push(cityState);
    if (p.zipCode) parts.push(p.zipCode);
    return parts.join(", ");
}

interface PropertiesTabProps {
    openDetailId?: number | null;
    onDetailConsumed?: () => void;
}

export function PropertiesTab({ openDetailId, onDetailConsumed }: PropertiesTabProps = {}) {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [page, setPage] = useState(1);
    const [showCreate, setShowCreate] = useState(false);
    const [editProp, setEditProp] = useState<Property | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Property | null>(null);
    const [detailProperty, setDetailProperty] = useState<Property | null>(null);
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Debounce search to avoid a request on every keystroke
    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 300);
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, [search]);

    const { properties, total, totalPages, isLoading, isPlaceholderData, createProperty, updateProperty, deleteProperty, isCreating, isUpdating } = useProperties({ search: debouncedSearch, page });

    // Open detail drawer when navigated from another tab — fetch by ID if not in current page
    useEffect(() => {
        if (openDetailId == null) return;
        const p = properties.find((pr: Property) => pr.id === openDetailId);
        if (p) {
            setDetailProperty(p);
            onDetailConsumed?.();
        } else if (!isLoading) {
            apiClient.properties.getProperty({ id: openDetailId }).then((fetched) => {
                setDetailProperty(fetched);
                onDetailConsumed?.();
            }).catch(() => {
                onDetailConsumed?.();
            });
        }
    }, [openDetailId, properties, isLoading, onDetailConsumed]);

    // Clamp page when total shrinks (e.g. deletion reduces page count)
    useEffect(() => {
        if (totalPages > 0 && page > totalPages) {
            setPage(totalPages);
        }
    }, [totalPages, page]);

    const handleDelete = () => {
        if (!deleteTarget) return;
        deleteProperty(deleteTarget.id);
        setDeleteTarget(null);
    };

    return (
        <TooltipProvider delayDuration={300}>
            <div className="space-y-4">
                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="relative w-full sm:max-w-xs">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input placeholder="Search properties..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" />
                    </div>
                    <Button size="sm" className="w-full sm:w-auto" onClick={() => setShowCreate(true)}>
                        <Plus className="h-4 w-4 mr-1.5" />
                        Add Property
                    </Button>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}</div>
                ) : properties.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="rounded-full bg-muted p-4 mb-4">
                                <MapPin className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-lg font-medium mb-1">No properties found</h3>
                            <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                                {search ? "Try a different search term." : "Add your first property to start building your directory."}
                            </p>
                            {!search && (
                                <Button size="sm" onClick={() => setShowCreate(true)}>
                                    <Plus className="h-4 w-4 mr-1.5" />Add Property
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <Card className="overflow-hidden">
                        <Table className="min-w-[620px] md:min-w-0 table-fixed">
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[40%]">Address</TableHead>
                                    <TableHead className="w-[25%]">City</TableHead>
                                    <TableHead className="w-[15%]">State</TableHead>
                                    <TableHead className="w-[15%]">ZIP</TableHead>
                                    <TableHead className="w-[40px]"><span className="sr-only">Actions</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {properties.map((p: Property) => (
                                    <TableRow key={p.id} tabIndex={0} className="group hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset" onClick={() => setDetailProperty(p)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setDetailProperty(p); } }}>
                                        <TableCell className="font-medium truncate">
                                            <span className="flex items-center gap-2 truncate">
                                                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                                                {p.streetAddress}{p.unit ? ` ${p.unit}` : ""}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground truncate">{p.city || <span className="text-muted-foreground/40">—</span>}</TableCell>
                                        <TableCell className="text-muted-foreground truncate">{p.state || <span className="text-muted-foreground/40">—</span>}</TableCell>
                                        <TableCell className="text-muted-foreground truncate">{p.zipCode || <span className="text-muted-foreground/40">—</span>}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" aria-label="Delete property" onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}>
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Delete</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <div className="px-4 py-3 border-t flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">
                                {total} propert{total !== 1 ? "ies" : "y"}
                                {totalPages > 1 && ` · Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)}`}
                            </span>
                            {totalPages > 1 && (
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || isPlaceholderData}>
                                        <ChevronLeft className="h-4 w-4 mr-1" />Previous
                                    </Button>
                                    <span className="text-xs text-muted-foreground px-2">Page {page} of {totalPages}</span>
                                    <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || isPlaceholderData}>
                                        Next<ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                {/* Create Dialog */}
                <PropertyCreateDialog
                    open={showCreate}
                    onClose={() => setShowCreate(false)}
                    createProperty={createProperty}
                    isCreating={isCreating}
                />

                {/* Edit Dialog */}
                <PropertyEditDialog
                    property={editProp}
                    onClose={() => setEditProp(null)}
                    updateProperty={updateProperty}
                    isUpdating={isUpdating}
                />

                {/* Delete Confirmation */}
                <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Delete property?</AlertDialogTitle>
                            <AlertDialogDescription>
                                This will permanently delete <span className="font-medium text-foreground">{deleteTarget ? formatAddress(deleteTarget) : ""}</span> and unlink it from any claims and contacts. This action cannot be undone.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                Delete
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>

            <PropertyDrawer
                property={detailProperty}
                open={!!detailProperty}
                onClose={() => setDetailProperty(null)}
                onEdit={(p) => { setDetailProperty(null); setEditProp(p); }}
            />
        </TooltipProvider>
    );
}